import { __ } from '@wordpress/i18n';
import { useBlockProps } from '@wordpress/block-editor';
import { Placeholder } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { store as blockEditorStore } from '@wordpress/block-editor';
import AllInspectorControls from './InspectorControls';
import { collectHeadings, buildHeadingTree } from '../headings';
import '../style.scss';

/**
 * One level of the outline. Recurses into `children`, producing the same
 * list-inside-list-item shape as the PHP renderer so both runtimes get the same
 * markers and numbering.
 */
function TocList({ items, ListTag, nested }) {
  return (
    <ListTag className="brandy-toc__list">
      {items.map((item) => (
        <li key={item.anchor} className={`brandy-toc__item is-level-${item.level}`}>
          <span className="brandy-toc__link">{item.text}</span>
          {nested && item.children?.length > 0 && (
            <TocList items={item.children} ListTag={ListTag} nested={nested} />
          )}
        </li>
      ))}
    </ListTag>
  );
}

/**
 * Sample outline for the inserter preview.
 *
 * This block draws its rows from the post it sits in, and a preview has no
 * post — so the inserter would otherwise advertise the block with the "add
 * some headings" placeholder, which shows nothing of what the block does.
 * These stand-ins are used ONLY when the editor says it is rendering a
 * preview and the surrounding blocks yield no headings of their own, so a
 * pattern preview that does contain headings still shows its real outline.
 */
const PREVIEW_HEADINGS = [
  { level: 2, text: __('Getting started', 'brandy-blocks'), anchor: 'getting-started' },
  { level: 3, text: __('What you need', 'brandy-blocks'), anchor: 'what-you-need' },
  { level: 3, text: __('Installing the theme', 'brandy-blocks'), anchor: 'installing-the-theme' },
  { level: 2, text: __('Making it yours', 'brandy-blocks'), anchor: 'making-it-yours' },
  { level: 3, text: __('Colours and type', 'brandy-blocks'), anchor: 'colours-and-type' },
  { level: 4, text: __('Palette presets', 'brandy-blocks'), anchor: 'palette-presets' },
  { level: 2, text: __('Going live', 'brandy-blocks'), anchor: 'going-live' },
];

/**
 * Editor preview.
 *
 * Reads the post's real headings from the editor store so the outline updates
 * as the author writes, rather than showing dummy rows. The list is rebuilt on
 * every change to the block tree; the frontend does the authoritative walk in
 * PHP.
 */
export default function Edit({ attributes, setAttributes }) {
  const { title, minLevel, maxLevel, listStyle, nested, collapsible } = attributes;

  const min = Math.min(minLevel, maxLevel);
  const max = Math.max(minLevel, maxLevel);

  const { headings, isPreview } = useSelect(
    (select) => {
      const { getBlocks, getSettings } = select(blockEditorStore);
      return {
        headings: collectHeadings(getBlocks(), min, max),
        // Set by BlockPreview; true for inserter cards, pattern previews and
        // the Site Editor's non-editable renders.
        isPreview: !!getSettings().isPreviewMode,
      };
    },
    [min, max]
  );

  const items =
    headings.length || !isPreview
      ? headings
      : PREVIEW_HEADINGS.filter((h) => h.level >= min && h.level <= max);

  const blockProps = useBlockProps({
    className: [
      'brandy-toc',
      `is-list-${listStyle}`,
      nested ? '' : 'is-flat',
      collapsible ? 'is-collapsible' : '',
    ]
      .filter(Boolean)
      .join(' '),
  });

  const ListTag = listStyle === 'numbered' ? 'ol' : 'ul';

  return (
    <>
      <AllInspectorControls
        attributes={attributes}
        setAttributes={setAttributes}
      />

      <nav {...blockProps}>
        {title && (
          <p className="brandy-toc__header">
            <span className="brandy-toc__title">{title}</span>
            {collapsible && (
              <i className="ph ph-caret-down brandy-toc__caret" aria-hidden="true" />
            )}
          </p>
        )}

        {items.length ? (
          <div className="brandy-toc__body">
            {/*
              The preview renders the same nested lists the front end does.
              It used to draw a single flat list with padding standing in for
              the nesting, which made list markers and numbering differ between
              the editor and the published page.
            */}
            <TocList
              items={nested ? buildHeadingTree(items) : items}
              ListTag={ListTag}
              nested={nested}
            />
          </div>
        ) : (
          <Placeholder
            className="brandy-toc__placeholder"
            label={__('Table of contents', 'brandy-blocks')}
            instructions={__(
              'Add headings to this post and they will appear here. The block renders nothing on the front end while there are none.',
              'brandy-blocks'
            )}
          />
        )}
      </nav>
    </>
  );
}
